/**
 * CardSelector Component
 * 
 * A grid of selectable cards for single-choice selection with icons and descriptions.
 * 
 * @param {string} label - Label text for the selector
 * @param {*} value - Currently selected value
 * @param {function} onChange - Callback when selection changes
 * @param {Array} options - Array of options: [{id, label, description?, icon?}]
 * @param {string} color - Accent color: "blue", "rose", "amber", "indigo" (default: "indigo")
 * @param {number} columns - Number of columns: 2, 3, 4 (default: 2)
 * @param {boolean} showCheckmark - Show checkmark on selected card (default: false)
 */

const COLOR_CLASSES = {
  blue: {
    selected: "bg-blue-50 border-blue-500 text-gray-900",
    icon: "bg-blue-100 text-blue-600",
    iconSelected: "bg-blue-500 text-white",
  },
  rose: {
    selected: "bg-rose-50 border-rose-500 text-gray-900",
    icon: "bg-rose-100 text-rose-600",
    iconSelected: "bg-rose-500 text-white",
  },
  amber: {
    selected: "bg-amber-50 border-amber-500 text-gray-900",
    icon: "bg-amber-100 text-amber-600",
    iconSelected: "bg-amber-500 text-white",
  },
  indigo: {
    selected: "bg-indigo-50 border-indigo-500 text-gray-900",
    icon: "bg-indigo-100 text-indigo-600",
    iconSelected: "bg-indigo-500 text-white",
  },
};

const COLUMN_CLASSES = {
  2: "grid-cols-2",
  3: "grid-cols-1 md:grid-cols-3",
  4: "grid-cols-2 md:grid-cols-4",
};

export default function CardSelector({
  label = null,
  value,
  onChange,
  options = [],
  color = "indigo",
  columns = 2,
  showCheckmark = false,
}) {
  const colors = COLOR_CLASSES[color] || COLOR_CLASSES.indigo;
  const columnClass = COLUMN_CLASSES[columns] || COLUMN_CLASSES[2];

  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm font-medium text-gray-700">{label}</label>
      )}
      
      <div className={`grid gap-2 ${columnClass}`}>
        {options.map((option) => {
          const isSelected = value === option.id;

          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(option.id)}
              className={`
                relative p-3 rounded-lg text-left transition-all duration-200 border
                ${isSelected
                  ? colors.selected
                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                }
              `}
            >
              {/* Checkmark */}
              {showCheckmark && isSelected && (
                <div className="absolute top-2 right-2">
                  <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Icon (emoji or custom) */}
              {option.icon && (
                <span className="text-xl block mb-1">{option.icon}</span>
              )}

              {/* Label */}
              <span className="font-medium text-sm block">{option.label}</span>

              {/* Description */}
              {option.description && (
                <span className="text-xs text-gray-400">{option.description}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}